import React, { useState, useEffect, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  IconTemplate,
  IconChevronDown,
  IconChevronRight,
  IconList,
  IconPencil,
  IconX,
  IconGripVertical,
} from "@tabler/icons-react";
import {
  Box,
  Group,
  Text,
  Paper,
  useMantineTheme,
  Title,
  ActionIcon,
  Button,
  Badge,
  ScrollArea,

} from "@mantine/core";
import moment from "moment";
import useStartupStore from "../stores/startupStore";
import fetchApi from "../components/Lib/api";
import RoadmapPDFGenerator from "../components/Startups/RoadmapPDFGenerator";
import EditCategoryDrawer from "./EditCategoryDrawer";

// Helper function to parse dates in the local timezone without day shifting
function parseLocalDate(dateString) {
  if (!dateString) return null;

  // Remove any time component if it exists
  const datePart = dateString.split("T")[0];

  // Split the date into components
  const [year, month, day] = datePart.split("-").map(Number);

  // Create a date using local timezone (months are 0-indexed in JS)
  return new Date(year, month - 1, day);
}

// Sortable Item component for categories
const SortableCategory = ({
  category,
  index,
  isEditMode,
  expandedSection,
  active,
  handleClick,
  renderSubItems,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `category-${category.id}` });

  const theme = useMantineTheme();
  const isExpanded = expandedSection === index;
  const isParentActive = isExpanded;
  const isItemActive = active === index;

  console.table(expandedSection, active, index);

  // Styles
  const styles = {
    root: {
      position: "relative",
      height: "fit-content",
    },
    container: {
      padding: theme.spacing.xl,
      backgroundColor: theme.white,
      borderRadius: theme.radius.lg,
      border: "1px solid #dee8e7",
      marginBottom: "8px",
      minWidth: "260px",
      maxWidth: "280px",
    },
    linkContainer: {
      position: "relative",
      paddingLeft: "2px",
    },
    link: {
      display: "flex",
      flexDirection: "column",
      textDecoration: "none",
      color: theme.colors.gray[7],
      fontSize: "0.9rem", // align with center tabs
      minHeight: "48px",
      borderTopRightRadius: theme.radius.md,
      borderBottomRightRadius: theme.radius.md,
      borderLeft: `2px solid ${theme.colors.gray[3]}`,
      transition: "all 200ms ease",
      marginBottom: "8px",
      padding: theme.spacing.xs,
    },
    linkContent: {
      display: "flex",
      alignItems: "center",
      fontWeight: 600, // bold like tabs
    },
    linkDate: {
      fontSize: theme.fontSizes.sm,
      color: theme.colors.gray[6],
      marginTop: "2px",
      marginLeft: "28px",
    },
    linkActive: {
      fontWeight: 600,
      color: "var(--color-navigation-icon-light)",
      backgroundColor: "rgba(108, 69, 231, 0.08)", // subtle brand tint
      borderLeft: "2px solid var(--color-navigation-icon-light)",
    },
    subItem: {
      paddingLeft: "28px",
      marginTop: "4px",
    },
    header: {
      marginBottom: theme.spacing.xl,
      borderBottom: `2px solid ${theme.colors.gray[2]}`,
      paddingBottom: theme.spacing.md,
    },
    icon: {
      marginRight: theme.spacing.sm,
      flexShrink: 0,
      color: theme.colors.gray[6],
    },
    activeIcon: {
      color: theme.colors.blue[6],
    },
    chevron: {
      marginLeft: "auto",
      flexShrink: 0,
      marginRight: theme.spacing.xs,
    },
    subItemsContainer: {
      marginTop: "4px",
    },
    editButton: {
      marginLeft: "auto",
    },
    addCategoryButton: {
      marginTop: theme.spacing.md,
      width: "100%",
    },
    gripHandle: {
      display: isEditMode ? "flex" : "none",
      cursor: "grab",
      marginRight: theme.spacing.xs,
    },
  };

  // Function to find status of category
  const findStatus = () => {
    let hasInProgress = false;
    let hasReview = false;
    let hasOpen = false;

    category.sub_categories.forEach((subItem) => {
      if (subItem.status === "InProgress") hasInProgress = true;
      if (subItem.status === "Review") hasReview = true;
      if (subItem.status === "Open") hasOpen = true;
    });

    if (hasInProgress || hasReview) return "PR"; // If any subcategory is InProgress or Review
    if (hasOpen) return "TD"; // If any subcategory is Open
    return "DN"; // If all are Closed
  };

  const curStatus = findStatus();

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : "auto",
  };

  return (
    <Box key={category.name} ref={setNodeRef} style={dragStyle}>
      <Box
        component="a"
        href={category.name}
        onClick={(e) => handleClick(e, category, index)}
        sx={(theme) => ({
          ...styles.link,
          ...(isParentActive || isItemActive ? styles.linkActive : {}),
          "&:hover": {
            backgroundColor:
              theme.colorScheme === "dark"
                ? theme.fn.rgba(theme.colors.dark[5], 0.7)
                : theme.fn.rgba(theme.colors.gray[0], 0.7),
            transform: "translateX(4px)",
          },
        })}
      >
        <div style={styles.linkContent}>
          {isEditMode && (
            <IconGripVertical
              size={18}
              style={styles.gripHandle}
              {...attributes}
              {...listeners}
            />
          )}
          <IconTemplate
            size={20}
            style={{
              ...styles.icon,
              ...(isParentActive || isItemActive ? styles.activeIcon : {}),
            }}
          />
          <Text>{category.name}</Text>
          {category.sub_categories?.length > 0 &&
            (isEditMode ? (
              <IconPencil size={18} style={styles.chevron} />
            ) : isExpanded ? (
              <IconChevronDown size={18} style={styles.chevron} />
            ) : (
              <IconChevronRight size={18} style={styles.chevron} />
            ))}
        </div>
        <div className={`flex justify-between gap-1 items-baseline`}>
          {category.start_date && (
            <Text style={styles.linkDate}>
              {moment(new Date(category.end_date)).format("MMM Do, YYYY")}
            </Text>
          )}
          <div className="flex gap-1">
            {category.notion_page_id && (
              <img
                src="/notion_logo.svg"
                alt="Notion"
                className="w-4 h-4 ml-2"
              />
            )}
            <Badge
              variant="filled"
              style={{
                backgroundColor:
                  curStatus === "TD"
                    ? "#0251cc"
                    : curStatus === "PR"
                    ? "#ea9713"
                    : "#63ba3c",
                color: "white", // ensures text is readable
              }}
              size="xs"
            >
              {curStatus}
            </Badge>
          </div>
        </div>
      </Box>
      {isExpanded && category.sub_categories && renderSubItems(category, index)}
    </Box>
  );
};

// Sortable Item component for subcategories
const SortableSubcategory = ({
  subItem,
  subIndex,
  parentIndex,
  isEditMode,
  handleClick,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `subcategory-${subItem.id}` });

  const theme = useMantineTheme();

  // Styles
  const styles = {
    root: {
      position: "relative",
      height: "fit-content",
    },
    linkContainer: {
      position: "relative",
      paddingLeft: "2px",
    },
    link: {
      display: "flex",
      flexDirection: "column",
      textDecoration: "none",
      color: theme.colors.gray[7],
       fontSize: "0.9rem", // align with center tabs
      minHeight: "48px",
      borderTopRightRadius: theme.radius.md,
      borderBottomRightRadius: theme.radius.md,
      borderLeft: `2px solid ${theme.colors.gray[3]}`,
      transition: "all 200ms ease",
      marginBottom: "8px",
      padding: theme.spacing.xs,
    },
    linkContent: {
      display: "flex",
      alignItems: "center",
      fontWeight: 600,
    },
    linkDate: {
      fontSize: theme.fontSizes.sm,
      color: theme.colors.gray[6],
      marginTop: "2px",
      marginLeft: "28px",
    },
    subItem: {
      paddingLeft: "28px",
      marginTop: "4px",
    },
    icon: {
      marginRight: theme.spacing.sm,
      flexShrink: 0,
      color: theme.colors.gray[6],
    },
    gripHandle: {
      display: isEditMode ? "flex" : "none",
      cursor: "grab",
      marginRight: theme.spacing.xs,
    },
  };

  // Function to find status of subcategory
  const findStatus = () => {
    if (subItem.status === "Open") return "TD";
    else if (subItem.status === "InProgress" || subItem.status === "Review")
      return "PR";
    else return "DN";
  };

  const curStatus = findStatus();

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : "auto",
  };

  return (
    <Box ref={setNodeRef} style={dragStyle}>
      <Box
        component="a"
        href={subItem.name}
        onClick={(e) => handleClick(e, subItem, subIndex, true, parentIndex)}
        sx={(theme) => ({
          ...styles.link,
          ...styles.subItem,
          "&:hover": {
            backgroundColor:
              theme.colorScheme === "dark"
                ? theme.fn.rgba(theme.colors.dark[5], 0.7)
                : theme.fn.rgba(theme.colors.gray[0], 0.7),
            transform: "translateX(4px)",
          },
        })}
      >
        <div style={styles.linkContent}>
          {isEditMode && (
            <IconGripVertical
              size={18}
              style={styles.gripHandle}
              {...attributes}
              {...listeners}
            />
          )}
          <IconTemplate size={20} style={styles.icon} />
          <Text>{subItem.name}</Text>
        </div>
        <div className="flex justify-end gap-1 items-baseline">
          {subItem.start_date && (
            <Text style={styles.linkDate}>
              {moment(new Date(subItem.end_date)).format("MMM Do, YYYY")}
            </Text>
          )}
          <Badge
            variant="filled"
            style={{
              backgroundColor:
                curStatus === "TD"
                  ? "#0251cc"
                  : curStatus === "PR"
                  ? "#ea9713"
                  : "#63ba3c",
              color: "white", // ensure text is readable
            }}
            size="xs"
          >
            {curStatus}
          </Badge>
        </div>
      </Box>
    </Box>
  );
};

export default function TableOfContentsFloating({editMode, disableEditIcon = false}) {
  const [active, setActive] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [isEditMode, setIsEditMode] = useState(editMode);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isNewCategory, setIsNewCategory] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [activeItem, setActiveItem] = useState(null);

  const {
    setSelectedProgram,
    program,
    setCategory,
    isOwner,
    slug,
    isMember,
    setProgram,
    isSubmitted,
  } = useStartupStore((state) => ({
    program: state.program,
    setCategory: state.setCategory,
    setSelectedProgram: state.setSelectedProgram,
    isOwner: state.isOwner,
    isMember: state.isMember,
    slug: state.slug,
    setProgram: state.setProgram,
    isSubmitted: state.isSubmitted,
  }));


  const theme = useMantineTheme();

  // Setup sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleNotionConnect = () => {
    window.location.href = "/auth/notion";
  };

  const handleUpdateDates = async () => {
    if (!selectedCategory && !isNewCategory) return;

    // Only update dates for existing categories
    if (!isNewCategory) {
      try {
        const response = await fetchApi(
          `/categories/update_dates/${selectedCategory.id}`,
          "PATCH",
          { category: { start_date: newStartDate, end_date: newEndDate } }
        );
        if (response.ok) {
          await response.json();
          setDrawerOpened(false);
        } else {
          const msg = (await response.json()).error;
          console.error(msg);
        }
      } catch (error) {
        console.error("Error during date change:", error);
      }
    }
  };

  // Styles
  const styles = {
    root: {
      position: "relative",
      height: "fit-content",
    },
    container: {
      padding: theme.spacing.xl,
      marginBottom: "8px",
      minWidth: "300px",
      maxWidth: "310px",
      background: "#ffffff",
      border: " 1px solid #eaeaea",
      borderRadius: "8px",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04",
    },
    header: {
      marginBottom: theme.spacing.xl,
      borderBottom: `2px solid ${theme.colors.gray[2]}`,
      paddingBottom: theme.spacing.md,
    },
    icon: {
      marginRight: theme.spacing.sm,
      flexShrink: 0,
      color: theme.colors.gray[6],
    },
    editButton: {
      marginLeft: "auto",
    },
    addCategoryButton: {
      marginTop: theme.spacing.md,
      width: "100%",
    },
    subItemsContainer: {
      marginTop: "4px",
    },
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setIsNewCategory(false);
    setDrawerOpened(true);
  };

  const handleNewCategory = () => {
    // Reset the form fields for a new category
    setSelectedCategory(null);
    setIsNewCategory(true);
    setDrawerOpened(true);
  };
  const handleClick = (
    event,
    item,
    index,
    isSubItem = false,
    parentIndex = null
  ) => {
    event.preventDefault();
    if (isEditMode) {
      if (!isSubItem) {
        handleEditCategory(item);
      }
      return;
    }

    if (!parentIndex && !isSubItem && item?.sub_categories?.length > 0) {
      setExpandedSection(expandedSection === index ? null : index);
      setActive(index);
    }

    if (!isSubItem) {
      setSelectedProgram({
        cid: index,
        sid: 0,
        switch: true,
      });
    } else {
      setSelectedProgram({
        cid: parentIndex,
        sid: index,
        switch: true,
      });
    }
  };

  const renderSubItems = (category, parentIndex) => {
    return (
      <div style={styles.subItemsContainer}>
        <SortableContext
          items={category.sub_categories.map((sub) => `subcategory-${sub.id}`)}
          strategy={verticalListSortingStrategy}
        >
          {category.sub_categories.map((subItem, subIndex) => (
            <SortableSubcategory
              key={`subcategory-${subItem.id}`}
              subItem={subItem}
              subIndex={subIndex}
              parentIndex={parentIndex}
              isEditMode={isEditMode}
              handleClick={handleClick}
            />
          ))}
        </SortableContext>
      </div>
    );
  };

  // Handle drag start
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);

    if (active.id.toString().startsWith("category-")) {
      const categoryId = active.id.split("-")[1];
      const category = program.categories.find(
        (cat) => cat.id.toString() === categoryId
      );
      setActiveItem(category);
    } else if (active.id.toString().startsWith("subcategory-")) {
      const subcategoryId = active.id.split("-")[1];
      // Find the subcategory across all categories
      let foundSubcategory = null;
      program.categories.forEach((category) => {
        const subcat = category.sub_categories.find(
          (sub) => sub.id.toString() === subcategoryId
        );
        if (subcat) foundSubcategory = subcat;
      });
      setActiveItem(foundSubcategory);
    }
  };

  // Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      // Handle category reordering
      if (
        active.id.toString().startsWith("category-") &&
        over.id.toString().startsWith("category-")
      ) {
        const oldIndex = program.categories.findIndex(
          (cat) => `category-${cat.id}` === active.id
        );
        const newIndex = program.categories.findIndex(
          (cat) => `category-${cat.id}` === over.id
        );

        if (oldIndex !== -1 && newIndex !== -1) {
          const newCategories = arrayMove(
            program.categories,
            oldIndex,
            newIndex
          );

          // Update the local state first for better UX
          setProgram({
            ...program,
            categories: newCategories,
          });

          // Then send the update to the backend
          try {
            await fetchApi(
              `/startup_entities/${slug}/categories/reorder`,
              "PATCH",
              {
                category_ids: newCategories.map((cat) => cat.id),
              }
            );
          } catch (error) {
            console.error("Error reordering categories:", error);
            // You might want to revert the state change on error
          }
        }
      }

      // Handle subcategory reordering
      if (
        active.id.toString().startsWith("subcategory-") &&
        over.id.toString().startsWith("subcategory-")
      ) {
        const activeSubId = active.id.split("-")[1];
        const overSubId = over.id.split("-")[1];

        // Find which category contains these subcategories
        let categoryIndex = -1;
        const categoryWithSubs = program.categories.find((category, idx) => {
          const hasActiveSub = category.sub_categories.some(
            (sub) => sub.id.toString() === activeSubId
          );
          const hasOverSub = category.sub_categories.some(
            (sub) => sub.id.toString() === overSubId
          );
          if (hasActiveSub && hasOverSub) {
            categoryIndex = idx;
            return true;
          }
          return false;
        });

        if (categoryWithSubs) {
          const oldSubIndex = categoryWithSubs.sub_categories.findIndex(
            (sub) => sub.id.toString() === activeSubId
          );
          const newSubIndex = categoryWithSubs.sub_categories.findIndex(
            (sub) => sub.id.toString() === overSubId
          );

          if (oldSubIndex !== -1 && newSubIndex !== -1) {
            const newSubcategories = arrayMove(
              categoryWithSubs.sub_categories,
              oldSubIndex,
              newSubIndex
            );

            // Create a copy of the categories array
            const newCategories = [...program.categories];
            // Update the specific category with the reordered subcategories
            newCategories[categoryIndex] = {
              ...newCategories[categoryIndex],
              sub_categories: newSubcategories,
            };

            // Update the local state
            setProgram({
              ...program,
              categories: newCategories,
            });

            // Send the update to the backend
            try {
              await fetchApi(
                `/startup_entities/${slug}/categories/${categoryWithSubs.id}/sub_categories/reorder`,
                "PATCH",
                {
                  sub_category_ids: newSubcategories.map((sub) => sub.id),
                }
              );
            } catch (error) {
              console.error("Error reordering subcategories:", error);
            }
          }
        }
      }
    }

    setActiveId(null);
    setActiveItem(null);
  };

  return (
    <Paper style={styles.root} p={0}>
      <div style={styles.container}>
        <Group noWrap style={styles.header}>
          <IconList size={28} stroke={1.5} style={styles.icon} />
          <Title order={4}>Project Roadmap</Title>
          {(isOwner || isMember) && !isSubmitted && !disableEditIcon && (
            <ActionIcon
              onClick={() => setIsEditMode(!isEditMode)}
              style={styles.editButton}
              variant="subtle"
            >
              {isEditMode ? <IconX size={18} /> : <IconPencil size={18} />}
            </ActionIcon>
          )}
        </Group>

        <ScrollArea.Autosize
          type="always"
          mah={500}
          maw={300}
          mx="auto"
          offsetScrollbars
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={
                program?.categories?.map((cat) => `category-${cat.id}`) || []
              }
              strategy={verticalListSortingStrategy}
            >
              {program?.categories?.map((category, index) => (
                <SortableCategory
                  key={`category-${category.id}`}
                  category={category}
                  index={index}
                  isEditMode={isEditMode}
                  expandedSection={expandedSection}
                  active={active}
                  handleClick={handleClick}
                  renderSubItems={renderSubItems}
                />
              ))}
            </SortableContext>

            {/* Drag overlay for visual feedback */}
            <DragOverlay>
              {activeId && activeItem ? (
                activeId.toString().startsWith("category-") ? (
                  <Box
                    sx={(theme) => ({
                      padding: theme.spacing.xs,
                      backgroundColor: theme.white,
                      borderRadius: theme.radius.md,
                      boxShadow: theme.shadows.sm,
                      border: `1px solid ${theme.colors.gray[3]}`,
                      width: "260px",
                    })}
                  >
                    <Group>
                      <IconTemplate size={20} />
                      <Text>{activeItem.name}</Text>
                    </Group>
                  </Box>
                ) : (
                  <Box
                    sx={(theme) => ({
                      padding: theme.spacing.xs,
                      backgroundColor: theme.white,
                      borderRadius: theme.radius.md,
                      boxShadow: theme.shadows.sm,
                      border: `1px solid ${theme.colors.gray[3]}`,
                      width: "260px",
                      marginLeft: "28px",
                    })}
                  >
                    <Group>
                      <IconTemplate size={20} />
                      <Text>{activeItem.name}</Text>
                    </Group>
                  </Box>
                )
              ) : null}
            </DragOverlay>
          </DndContext>
        </ScrollArea.Autosize>

        {/* Add New Category Button when in edit mode */}
        {isEditMode && (
          <Button
            onClick={handleNewCategory}
            style={styles.addCategoryButton}
            variant="outline"
          >
            Add New Category
          </Button>
        )}
      </div>

      {isMember && <RoadmapPDFGenerator slug={slug} />}

      {/* Edit Category Modal */}
      <EditCategoryDrawer
      drawerOpened={drawerOpened}
      setDrawerOpened={setDrawerOpened}
      selectedCategory={selectedCategory}
      setSelectedCategory={setSelectedCategory}
      isNewCategory={isNewCategory}
      setIsNewCategory={setIsNewCategory}
    />
    </Paper>
  );
}
